import React from "react";
import LocationField from "../../../components/commons/LocationField";
import DropoffLocationField from "./DropoffLocationField";
import MOButtonV2 from "../../../components/commons/MOButtonV2";
import ButtonToggle from "../../../components/commons/ButtonToggle";
import tripCityList from "../../../data/tripCityList";
import {
  CURRENCY_OPTIONS,
  TRIP_TYPES,
  TRIP_TYPE_LABELS,
  isAirportPickup,
} from "./utils";

const RateAnalysisForm = ({
  values,
  errors,
  loading,
  onChange,
  onSubmit,
}) => {
  const isPickup = isAirportPickup(values.tripType);
  const hasSelectedCity = Boolean(values.cityName);
  const selectedCity = tripCityList.find((city) => city.Name === values.cityName);

  const handleLocationChange = (field) => (value) => {
    onChange(field, { name: value || "" });
  };

  const handleTripTypeToggle = (label) => {
    const tripType =
      label === TRIP_TYPE_LABELS[TRIP_TYPES.AIRPORT_DROPOFF]
        ? TRIP_TYPES.AIRPORT_DROPOFF
        : TRIP_TYPES.AIRPORT_PICKUP;
    onChange("tripType", tripType);
  };

  const handleCityChange = (e) => {
    const city = tripCityList.find((item) => item.Name === e.target.value);
    onChange("cityName", city?.Name || "");
    onChange("cityId", city?.Id ?? "");
    onChange("pickup", { name: "" });
    onChange("dropoff", { name: "" });
  };

  const minDate = new Date().toISOString().split("T")[0];
  const minDateTime = new Date().toISOString().slice(0, 16);

  return (
    <form
      className="rate-analysis-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="rate-analysis-form-top-row">
        <div className="rate-analysis-form-trip-type">
          <label className="rate-analysis-filter-toggle-label">Trip type</label>
          <ButtonToggle
            btnList={[
              TRIP_TYPE_LABELS[TRIP_TYPES.AIRPORT_PICKUP],
              TRIP_TYPE_LABELS[TRIP_TYPES.AIRPORT_DROPOFF],
            ]}
            activeToggle={TRIP_TYPE_LABELS[values.tripType]}
            setActiveToggle={handleTripTypeToggle}
          />
        </div>

        <div className="input-field-sm rate-analysis-field rate-analysis-form-city">
          <label htmlFor="rate-analysis-city">City</label>
          <select
            id="rate-analysis-city"
            value={values.cityName}
            onChange={handleCityChange}
          >
            <option value="">Select city</option>
            {tripCityList.map((city) => (
              <option key={`${city.Id}-${city.Name}`} value={city.Name}>
                {city.Name}
                {city.country ? ` — ${city.country}` : ""}
              </option>
            ))}
          </select>
          {errors.cityId && (
            <p className="text-danger font-12 mt-4">{errors.cityId}</p>
          )}
        </div>
      </div>

      {hasSelectedCity && (
        <>
          <div className="rate-analysis-form-locations">
            <div className="input_field rate-analysis-field">
              <p className="font-12 font-600 mb-6">
                {isPickup ? "Pickup (Airport)" : "Pickup Location"}
              </p>
              {isPickup ? (
                <>
                  <LocationField
                    location={values.pickup}
                    placeholder="Search airport pickup location"
                    onChange={handleLocationChange("pickup")}
                  />
                  {errors.pickup && (
                    <p className="text-danger font-12 mt-4">{errors.pickup}</p>
                  )}
                </>
              ) : (
                <DropoffLocationField
                  cityId={values.cityId}
                  cityCode={selectedCity?.code}
                  pickup={values.dropoff}
                  currency={values.currency}
                  location={values.pickup}
                  onChange={(value) => onChange("pickup", value)}
                  placeholder="Search pickup location"
                  error={errors.pickup}
                  disabled={!values.cityId}
                />
              )}
            </div>

            <div className="input_field rate-analysis-field">
              <p className="font-12 font-600" style={{ marginBottom: "13px" }}>
                {isPickup ? "Drop-off Location" : "Drop-off (Airport)"}
              </p>
              {isPickup ? (
                <DropoffLocationField
                  cityId={values.cityId}
                  cityCode={selectedCity?.code}
                  pickup={values.pickup}
                  currency={values.currency}
                  location={values.dropoff}
                  onChange={(value) => onChange("dropoff", value)}
                  placeholder="Search drop-off location"
                  error={errors.dropoff}
                  disabled={!values.cityId}
                />
              ) : (
                <>
                  <LocationField
                    location={values.dropoff}
                    placeholder="Search airport drop-off location"
                    onChange={handleLocationChange("dropoff")}
                  />
                  {errors.dropoff && (
                    <p className="text-danger font-12 mt-4">{errors.dropoff}</p>
                  )}
                </>
              )}
            </div>
          </div>

          {isPickup ? (
            <div className="rate-analysis-form-details rate-analysis-form-details-4">
              <div className="input-field-sm rate-analysis-field">
                <label htmlFor="rate-analysis-currency">Currency</label>
                <select
                  id="rate-analysis-currency"
                  value={values.currency}
                  onChange={(e) => onChange("currency", e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.currency && (
                  <p className="text-danger font-12 mt-4">{errors.currency}</p>
                )}
              </div>

              <div className="input-field-sm rate-analysis-field">
                <label htmlFor="rate-analysis-flight">Flight Number</label>
                <input
                  id="rate-analysis-flight"
                  type="text"
                  placeholder="e.g. VS7012"
                  value={values.flightNumber}
                  onChange={(e) => onChange("flightNumber", e.target.value)}
                />
                {errors.flightNumber && (
                  <p className="text-danger font-12 mt-4">
                    {errors.flightNumber}
                  </p>
                )}
              </div>

              <div className="input-field-sm rate-analysis-field">
                <label htmlFor="rate-analysis-date">Pickup Date</label>
                <input
                  id="rate-analysis-date"
                  type="date"
                  value={values.date}
                  min={minDate}
                  onChange={(e) => onChange("date", e.target.value)}
                />
                {errors.date && (
                  <p className="text-danger font-12 mt-4">{errors.date}</p>
                )}
              </div>

              <div className="input-field-sm rate-analysis-field">
                <label htmlFor="rate-analysis-passengers-pickup">
                  Passenger Count
                </label>
                <input
                  id="rate-analysis-passengers-pickup"
                  type="number"
                  min={1}
                  max={99}
                  value={values.passengerCount}
                  onChange={(e) => onChange("passengerCount", e.target.value)}
                />
                {errors.passengerCount && (
                  <p className="text-danger font-12 mt-4">
                    {errors.passengerCount}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="rate-analysis-form-details">
              <div className="input-field-sm rate-analysis-field">
                <label htmlFor="rate-analysis-currency-dropoff">Currency</label>
                <select
                  id="rate-analysis-currency-dropoff"
                  value={values.currency}
                  onChange={(e) => onChange("currency", e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.currency && (
                  <p className="text-danger font-12 mt-4">{errors.currency}</p>
                )}
              </div>

              <div className="input-field-sm rate-analysis-field">
                <label htmlFor="rate-analysis-datetime">Pickup Date & Time</label>
                <input
                  id="rate-analysis-datetime"
                  type="datetime-local"
                  value={values.dateTime}
                  min={minDateTime}
                  onChange={(e) => onChange("dateTime", e.target.value)}
                />
                {errors.dateTime && (
                  <p className="text-danger font-12 mt-4">{errors.dateTime}</p>
                )}
              </div>

              <div className="input-field-sm rate-analysis-field">
                <label htmlFor="rate-analysis-passengers-dropoff">
                  Passenger Count
                </label>
                <input
                  id="rate-analysis-passengers-dropoff"
                  type="number"
                  min={1}
                  max={99}
                  value={values.passengerCount}
                  onChange={(e) => onChange("passengerCount", e.target.value)}
                />
                {errors.passengerCount && (
                  <p className="text-danger font-12 mt-4">
                    {errors.passengerCount}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="rate-analysis-form-actions">
            <MOButtonV2
              type="submit"
              text={loading ? "Searching..." : "Search Rates"}
              disabled={loading}
              loading={loading}
              backgroundColor="#29AEE6"
              textColor="#FFFFFF"
              border="none"
              radius={4}
              height={40}
              fontSize={14}
              padding="10px 28px"
            />
          </div>
        </>
      )}
    </form>
  );
};

export default RateAnalysisForm;
